import { IconComponent } from '@mainstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import clsx from 'clsx';
import { ReactNode, useContext } from 'react';
import {
  SubmenuConfig,
  SubmenuModeEnum,
  WbSidebarConfigContext,
  WbSidebarItemWithSubmenuContext,
} from './config';
import { useWbSidebarOpenState } from './WbSidebarOpenStateContext';
import { Divider } from '@material-ui/core';
import { WbButton } from '../WbButton';
import { useWbSidebarMobileState } from './WbNavbarMobileStateContext';
import { useNavigate } from 'react-router';
import { getOnHoverScrollbarStyle } from '../../styles';
import { useWbSidebarPinState } from './WbSidebarPinStateContext';

const useStyles = makeStyles<
  Theme,
  { submenuConfig: SubmenuConfig; left: number; isMobile: boolean }
>(
  theme => ({
    drawer: props => ({
      position: 'fixed',
      transition: theme.transitions.create(['width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shortest,
      }),
      overflowX: 'hidden',
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
      cursor: 'default',
      width: props.submenuConfig.drawerWidthClosed,
      transitionDelay: `${props.submenuConfig.defaultOpenDelayMs}ms`,
      '& > *': {
        flexShrink: 0,
      },
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    }),
    drawerDefault: {
      top: 0,
      bottom: 0,
      padding: 0,
    },
    drawerOver: props => ({
      top: props.isMobile ? 31 : 81,
      zIndex: 999,
      bottom: props.isMobile ? 0 : 0,
    }),
    backButton: {
      alignSelf: 'center',
      margin: '16px 0px',
      display: 'flex',
      gap: theme.spacing(1),
    },
    drawerOpen: props => ({
      width: props.isMobile ? '100%' : props.submenuConfig.drawerWidthOpen,
      borderRight: `1px solid ${theme.palette.bkg.secondary}`,
    }),
    drawerContent: props => ({
      width: props.isMobile ? '100%' : props.submenuConfig.drawerWidthOpen - 1,
      display: 'flex',
      flexFlow: 'column nowrap',
      alignItems: 'flex-start',
      height: '100%',
      backgroundColor: theme.palette.white,
      overflowY: 'auto',
      ...getOnHoverScrollbarStyle(theme, false),
    }),
    title: {
      color: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(2),
    },
  }),
  { name: 'WitboostSidebarSubmenu' },
);

export type WbSidebarSubmenuProps = {
  title?: string;
  icon?: IconComponent;
  children: ReactNode;
};

/**
 * Used inside SidebarItem to display an expandable Submenu
 *
 * @public
 */
export const WbSidebarSubmenu = (props: WbSidebarSubmenuProps) => {
  const { isOpen } = useWbSidebarOpenState();
  const { isMobile } = useWbSidebarMobileState();
  const { sidebarConfig, submenuConfig } = useContext(WbSidebarConfigContext);
  const { isSubmenuOpen, setIsSubMenuOpen, level, submenuMode } = useContext(
    WbSidebarItemWithSubmenuContext,
  );
  const { setTemporarilyPinned } = useWbSidebarPinState();
  // the current nesting level of the submenu (0 is the first submenu)
  const currLevel = level || 0;
  // compute the margin this submenu needs to be shifted on the left
  // if the parent is the main drawer shift it by the main drawer width, if it's a submenu itself by the submenu width
  const leftConfig = currLevel > 0 ? submenuConfig : sidebarConfig;
  const left = isOpen
    ? leftConfig.drawerWidthOpen
    : leftConfig.drawerWidthClosed;

  const classes = useStyles({ left, submenuConfig, isMobile });
  const navigate = useNavigate();
  const { icon: Icon, title, children } = props;

  return (
    <Box
      // shift the submenu by the parent width - 1 (or 2 if the parent is a submenu itself) to cover the parent borders,
      // thus preventing them to be hovered and accidentally close this submenu
      style={{
        marginLeft:
          submenuMode === SubmenuModeEnum.side
            ? left - (currLevel > 0 ? 2 : 1)
            : 0,
        display: isSubmenuOpen ? 'block' : 'none',
      }}
      className={clsx(
        classes.drawer,
        submenuMode === SubmenuModeEnum.overlay
          ? classes.drawerOver
          : classes.drawerDefault,
        {
          [classes.drawerOpen]: isSubmenuOpen,
        },
      )}
      aria-hidden={!isSubmenuOpen}
    >
      <div className={classes.drawerContent} aria-hidden={!isSubmenuOpen}>
        {submenuMode === SubmenuModeEnum.overlay && (
          <>
            <WbButton
              startIcon={<KeyboardBackspaceIcon />}
              className={classes.backButton}
              variant="outlined"
              color="primary"
              onClick={
                isMobile
                  ? () => {
                      setIsSubMenuOpen(false);
                      // Set temporarilyPinned to false for mobile navbar to update tabIndex
                      // of sidebar elements, making the sidebar navigable via Tab key for accessibility.
                      // On mobile, clicking submenu items doesn't change isTemporaryPinned automatically.
                      setTemporarilyPinned(false);
                    }
                  : () => navigate('/')
              }
              label={isMobile ? 'Back to Main Menu' : 'Back to Witboost'}
              tabIndex={isSubmenuOpen ? 0 : -1}
            />

            <Divider
              style={{ alignSelf: 'center', width: 'calc(100% - 32px)' }}
            />
          </>
        )}
        <Typography variant="body2" className={classes.title}>
          {Icon && <Icon />}
          {title}
        </Typography>
        {children}
      </div>
    </Box>
  );
};
